import React, { useState, useMemo } from 'react';
import { FileCode, Terminal, File, FileJson, ChevronDown, ChevronRight, Package, Database } from 'lucide-react';
import { useSupabaseActionParser, SupabaseAction } from '@/components/integrations';
import { useSupabaseMCP } from '@/hooks/useSupabaseMCP';
import { SupabaseInlineAction } from './SupabaseInlineAction';

interface BoltAction {
  type: 'file' | 'shell' | 'delete';
  filePath?: string;
  content: string;
}
interface BoltArtifact {
  id: string;
  title: string;
  actions: BoltAction[];
  supabaseActions?: SupabaseAction[];
}

// Parse boltArtifact XML from content
export function parseBoltArtifacts(content: string): {
  artifacts: BoltArtifact[];
  textContent: string;
} {
  // CRITICAL SAFETY: Ensure content is always a string to prevent .trim()/.search()/.length crashes
  const safeContent = content ?? '';

  const artifacts: BoltArtifact[] = [];
  let textContent = safeContent;

  // Debug logging - only when DEBUG is enabled
  const DEBUG_PARSER = false; // Set to true to debug artifact parsing
  const hasBoltArtifact = /<boltArtifact/i.test(safeContent);
  const hasClosingTag = /<\/boltArtifact>/i.test(safeContent);
  if (DEBUG_PARSER && hasBoltArtifact) {
    console.log('[parseBoltArtifacts] Parsing', safeContent.length, 'bytes, hasClosing:', hasClosingTag);
  }

  // Very flexible regex - matches boltArtifact with any attributes in any order
  // Handles whitespace, newlines, and various attribute patterns
  const artifactRegex = /<boltArtifact[^>]*>([\s\S]*?)<\/boltArtifact>/gi;

  let match;
  while ((match = artifactRegex.exec(safeContent)) !== null) {

    const [fullMatch, innerContent] = match;

    // Extract id and title from the opening tag
    const idMatch = fullMatch.match(/id=["']([^"']+)["']/i);
    const titleMatch = fullMatch.match(/title=["']([^"']+)["']/i);

    const id = idMatch?.[1] || 'artifact-' + artifacts.length;
    const artifactTitle = titleMatch?.[1] || 'Code généré';
    const actions: BoltAction[] = [];

    // Simplified regex patterns - matching Sandpack's working patterns
    // Pattern 1: type="file" filePath="..."
    const actionRegex1 = /<boltAction\s+type="file"\s+filePath="([^"]+)"\s*>([\s\S]*?)<\/boltAction>/gi;
    // Pattern 2: filePath="..." type="file"  
    const actionRegex2 = /<boltAction\s+filePath="([^"]+)"\s+type="file"\s*>([\s\S]*?)<\/boltAction>/gi;
    // Pattern 3: Shell commands
    const shellRegex = /<boltAction\s+type="shell"\s*>([\s\S]*?)<\/boltAction>/gi;
    // Pattern 4: Delete actions
    const deleteRegex = /<boltAction\s+type="delete"\s+filePath="([^"]+)"\s*\/?>/gi;

    let actionMatch;

    // Try pattern 1: type first
    while ((actionMatch = actionRegex1.exec(innerContent)) !== null) {
      const [, filePath, actionContent] = actionMatch;
      actions.push({
        type: 'file',
        filePath: filePath,
        content: actionContent.trim()
      });
    }

    // Try pattern 2: filePath first
    actionRegex2.lastIndex = 0;
    while ((actionMatch = actionRegex2.exec(innerContent)) !== null) {
      const [, filePath, actionContent] = actionMatch;
      // Don't add duplicates
      if (!actions.some(a => a.filePath === filePath)) {
        actions.push({
          type: 'file',
          filePath: filePath,
          content: actionContent.trim()
        });
      }
    }

    // Try shell commands
    shellRegex.lastIndex = 0;
    while ((actionMatch = shellRegex.exec(innerContent)) !== null) {
      const [, actionContent] = actionMatch;
      actions.push({
        type: 'shell',
        content: actionContent.trim()
      });
    }

    // Try delete actions
    deleteRegex.lastIndex = 0;
    while ((actionMatch = deleteRegex.exec(innerContent)) !== null) {
      const [, filePath] = actionMatch;
      actions.push({
        type: 'delete',
        filePath: filePath,
        content: ''
      });
    }

    // Check if there are supabase actions (even if no file/shell actions)
    const hasSupabaseActions = /type="supabase-(sql|create-table|alter-table|policy|edge-function|schema|logs)"/.test(innerContent);

    // Create artifact if there are any actions (file, shell, OR supabase)
    if (actions.length > 0 || hasSupabaseActions) {
      artifacts.push({
        id,
        title: artifactTitle,
        actions,
        supabaseActions: [] // Will be parsed separately by BoltArtifactRenderer
      });
    }

    // Remove artifact from text content
    textContent = textContent.replace(fullMatch, '');
  }

  // Strip any remaining bolt tags that weren't fully parsed
  textContent = textContent.replace(/<boltArtifact[^>]*>[\s\S]*?<\/boltArtifact>/gi, '');

  // FALLBACK: Check for bare supabase boltAction tags (not wrapped in boltArtifact)
  // This happens when AI generates SQL actions without proper wrapper
  const bareSupabaseActionRegex = /<boltAction\s+type="supabase-(sql|create-table|alter-table|policy|edge-function|schema|logs)"[^>]*>[\s\S]*?<\/boltAction>/gi;
  const hasBareSupabaseActions = bareSupabaseActionRegex.test(safeContent);

  if (hasBareSupabaseActions && artifacts.length === 0) {
    console.log('[parseBoltArtifacts] Found bare supabase actions without wrapper, creating synthetic artifact');
    // Create a synthetic artifact for bare supabase actions
    artifacts.push({
      id: 'synthetic-supabase-' + Date.now(),
      title: 'Actions Supabase',
      actions: [],  // No file/shell actions, only supabase
      supabaseActions: []  // Will be parsed by BoltArtifactRenderer
    });
  }

  textContent = textContent.replace(/<boltAction[^>]*>[\s\S]*?<\/boltAction>/gi, '');
  // Also strip orphaned opening/closing tags
  textContent = textContent.replace(/<\/?boltArtifact[^>]*>/gi, '');
  textContent = textContent.replace(/<\/?boltAction[^>]*>/gi, '');
  // Strip self-closing boltAction tags (e.g., <boltAction ... />)
  textContent = textContent.replace(/<boltAction[^>]*\/>/gi, '');

  // Strip <thinking> tags and their content
  textContent = textContent.replace(/<thinking>[\s\S]*?<\/thinking>/gi, '');
  textContent = textContent.replace(/<\/?thinking>/gi, '');

  // Debug: log artifact parsing results - only when artifacts found
  const totalActions = artifacts.reduce((sum, a) => sum + a.actions.length, 0);
  if (artifacts.length > 0 || DEBUG_PARSER) {
    console.log('[BoltArtifact]', artifacts.length, 'artifacts,', totalActions, 'actions');
  }

  return {
    artifacts,
    textContent: textContent.trim()
  };
}

// Get file extension from path
function getFileExtension(filePath: string): string {
  const parts = filePath.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

// Get file icon based on extension
function FileIcon({
  filePath
}: {
  filePath: string;
}) {
  const ext = getFileExtension(filePath);
  if (['tsx', 'ts', 'jsx', 'js'].includes(ext)) {
    return <FileCode className="h-3.5 w-3.5 text-foreground" />;
  }
  if (ext === 'json') {
    return <FileJson className="h-3.5 w-3.5 text-muted-foreground" />;
  }
  if (['css', 'scss'].includes(ext)) {
    return <File className="h-3.5 w-3.5 text-muted-foreground" />;
  }
  return <File className="h-3.5 w-3.5 text-faint" />;
}

export function BoltArtifactRenderer({
  artifact,
  rawContent,
  pixelProjectId
}: {
  artifact: BoltArtifact;
  rawContent?: string;
  pixelProjectId?: string;
}) {
  const [actionsExpanded, setActionsExpanded] = useState(true);
  const [summaryExpanded, setSummaryExpanded] = useState(false);

  const { parseActions } = useSupabaseActionParser();
  const { isConnected, selectedProjectName } = useSupabaseMCP(pixelProjectId);

  // Parse Supabase actions from raw content
  const supabaseActions = useMemo(() => {
    if (!rawContent) {
      return [];
    }
    const actions = parseActions(rawContent);

    if (actions.length > 0) {
      console.log('[BoltArtifactRenderer] Actions found:', actions.map(a => ({ type: a.type, name: a.name })));
    }
    return actions;
  }, [rawContent, parseActions]);

  const fileActions = artifact.actions.filter(a => a.type === 'file' && a.filePath);
  const shellActions = artifact.actions.filter(a => a.type === 'shell');
  const totalActions = fileActions.length + shellActions.length;
  const hasSupabaseActions = supabaseActions.length > 0;

  /* console.log('[BoltArtifactRenderer] Render:', {
    fileActions: fileActions.length,
    hasSupabaseActions,
    isConnected
  }); */

  if (totalActions === 0 && !hasSupabaseActions) {
    return null;
  }

  return (
    <div className="my-3 space-y-3">
      {/* Supabase Actions - Inline execution (if any) */}
      {supabaseActions.length > 0 && (
        <div className="border border-border overflow-hidden bg-card">
          <div className="px-3 py-2 font-mono text-[11px] uppercase tracking-wider text-faint border-b border-border flex items-center justify-between">
            <span>Actions Supabase ({supabaseActions.length})</span>
            {isConnected && selectedProjectName && (
              <span className="text-ok normal-case tracking-normal">→ {selectedProjectName}</span>
            )}
            {!isConnected && (
              <span className="text-warn normal-case tracking-normal">Connectez Supabase</span>
            )}
          </div>
          <div className="py-1">
            {supabaseActions.map((action, idx) => (
              <div key={`supabase-${idx}`} className="px-3 py-1.5">
                <SupabaseInlineAction
                  action={action}
                  pixelProjectId={pixelProjectId}
                  autoExecute={false}
                  onComplete={(success) => {
                    console.log(`[Supabase] Action ${idx + 1} completed:`, success);
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary Badge */}
      <div>
        <button
          onClick={() => setSummaryExpanded(!summaryExpanded)}
          className="flex items-center gap-2 px-3 py-1.5 bg-secondary border border-border hover:bg-surface-3 transition-colors text-sm"
        >
          <Package className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-foreground font-mono text-xs tabular-nums">{totalActions} modification{totalActions > 1 ? 's' : ''}</span>
          {summaryExpanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-faint ml-1" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-faint ml-1" />
          )}
        </button>

        {/* Expandable File Table */}
        {summaryExpanded && fileActions.length > 0 && (
          <div className="mt-2 border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-secondary border-b border-border">
                  <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider font-medium text-faint">Fichier</th>
                  <th className="px-3 py-2 text-left font-mono text-[11px] uppercase tracking-wider font-medium text-faint">Contenu</th>
                </tr>
              </thead>
              <tbody>
                {fileActions.map((action, idx) => (
                  <tr key={idx} className="border-b border-border last:border-0 bg-card">
                    <td className="px-3 py-2">
                      <code className="text-xs font-mono text-foreground bg-secondary px-1.5 py-0.5">
                        {action.filePath?.split('/').pop()}
                      </code>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">
                      {getFileDescription(action.filePath || '')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function getFileDescription(filePath: string): string {
  const fileName = filePath.split('/').pop() || '';
  const ext = fileName.split('.').pop()?.toLowerCase();

  if (fileName === 'App.tsx') return 'Composant principal React';
  if (fileName === 'main.tsx') return "Point d'entrée de l'application";
  if (fileName.includes('styles') || fileName.includes('index.css')) return 'Styles globaux';
  if (filePath.includes('components/')) return 'Composant React';
  if (ext === 'tsx' || ext === 'jsx') return 'Composant React';
  if (ext === 'ts' || ext === 'js') return 'Module JavaScript';
  if (ext === 'css') return 'Feuille de styles';
  if (ext === 'json') return 'Configuration';
  if (ext === 'html') return 'Document HTML';
  return 'Fichier source';
}

// Convenience component to render all artifacts from content
export function BoltArtifactsFromContent({
  content
}: {
  content: string;
}) {
  const {
    artifacts
  } = useMemo(() => parseBoltArtifacts(content), [content]);
  return <>
    {artifacts.map(artifact => <BoltArtifactRenderer key={artifact.id} artifact={artifact} rawContent={content} />)}
  </>;
}